// web/src/routes/Console.tsx — the Home / Vault landing route (M4.1).
//
// Home is a posture summary, not a splash (wireframe 01): a security-posture strip,
// a "needs you" derivation, and the records dashboard bound to api.listRecords —
// each row shows status, K/N, quorum health (from provenCount), and the caller's
// role. Row actions: open detail (/records/:id) and "Convene unseal" (opens a
// ceremony then routes to /unseal/:id). METADATA ONLY — no shard, key, or plaintext.
import React, { useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Plus, ShieldCheck, Fingerprint, KeyRound } from 'lucide-react';
import type {
  AuditEntry,
  Principal,
  RecordSummary,
  SealReceipt,
} from '../../../shared/types';
import type { VerifyResult } from '../components/AuditFeed';
import { EmptyState } from '../components/ui';
import { Icon } from '../components/ui/Icon';
import { PageHeader } from '../components/ui/PageHeader';
import { useAsync } from '../hooks';
import { api } from '../api';
import { RecordsTable } from '../components/records/RecordsTable';
import { BreakGlassDialog } from '../components/records/BreakGlassDialog';
import { useConveneUnseal } from '../components/records/useConveneUnseal';
import { ShareCeremonyPanel } from '../components/ceremony/ShareCeremonyPanel';
import { JoinUnsealModal } from '../components/ceremony/JoinUnsealModal';
import { quorumHealth } from '../lib/records';

/** A record sealed during this browser session (legacy receipt shape, kept for type compat). */
export interface SealedRecordView {
  receipt: SealReceipt;
  name: string;
}

export interface ConsoleData {
  principal: Principal;
  audit: AuditEntry[];
  sealedRecords: SealedRecordView[];
  /** Opens the seal wizard (new-secret → /enroll lobby). */
  onNewSecret: () => void;
  /** Verify the audit hash-chain (consumed by the Audit page). */
  onVerify: () => Promise<VerifyResult>;
}

export function useConsoleData(): ConsoleData {
  return useOutletContext<ConsoleData>();
}

interface StatProps {
  kicker: string;
  value: React.ReactNode;
  meta?: string;
  tone?: 'accent' | 'green' | 'amber' | 'red';
}
function Stat({ kicker, value, meta, tone }: StatProps): React.JSX.Element {
  const toneClass = tone
    ? { accent: 'bgStatAccent', green: 'bgStatGreen', amber: 'bgStatAmber', red: 'bgStatRed' }[tone]
    : '';
  return (
    <div className={`bgStat ${toneClass}`}>
      <span className="bgStatKicker">{kicker}</span>
      <span className="bgStatValue">{value}</span>
      {meta && <span className="bgStatMeta">{meta}</span>}
    </div>
  );
}

export function Console(): React.JSX.Element {
  const d = useConsoleData();
  const navigate = useNavigate();
  const recordsState = useAsync<RecordSummary[]>(api.listRecords);
  const records = recordsState.data ?? [];

  const convene = useConveneUnseal(d.principal);
  const [target, setTarget] = useState<RecordSummary | null>(null);
  const [joinOpen, setJoinOpen] = useState(false);

  const posture = useMemo(() => {
    let owned = 0;
    let healthy = 0;
    let attention = 0;
    for (const r of records) {
      if (r.isOwner) owned += 1;
      const h = quorumHealth(r);
      if (h.tone === 'success') healthy += 1;
      if (h.tone === 'warning' || h.tone === 'danger') attention += 1;
    }
    return { owned, healthy, attention, total: records.length };
  }, [records]);

  return (
    <div className="bgPage">
      <PageHeader
        kicker="EMERGENCY ACCESS"
        title="Sealed. Broken only by quorum."
        subtitle="The server only ever holds metadata and encrypted shards — your secrets are split and sealed in this browser."
        actions={
          <>
            <button type="button" className="btn" onClick={() => setJoinOpen(true)}>
              <Icon icon={KeyRound} size="sm" /> Join unseal
            </button>
            <button type="button" className="btn btn-primary" onClick={d.onNewSecret}>
              <Icon icon={Plus} size="sm" /> New secret
            </button>
          </>
        }
      />
      <p className="bgHomeTrust">
        <Icon icon={ShieldCheck} size="sm" tone="accent" /> Zero-trust vault — no single party can
        unseal alone.
      </p>

      <div className="bgStrip bgStrip4">
        <Stat kicker="Records" value={posture.total} meta="you own or hold a shard in" tone="accent" />
        <Stat kicker="You own" value={posture.owned} meta="responsible party" />
        <Stat
          kicker="Quorum healthy"
          value={posture.healthy}
          meta="recent proofs passed"
          tone="green"
        />
        <Stat
          kicker="Needs attention"
          value={posture.attention}
          meta="proof, re-deal, or margin"
          tone={posture.attention > 0 ? 'amber' : undefined}
        />
      </div>

      <section className="bgSection" aria-label="Records">
        <header className="bgSectionHead">
          <div>
            <div className="kicker">Your records</div>
            <h2 className="bgSectionTitle">Records dashboard</h2>
          </div>
          <button type="button" className="btn btn-sm" onClick={() => navigate('/proofs')}>
            <Icon icon={Fingerprint} size="xs" /> Shard proofs
          </button>
        </header>

        {recordsState.loading && records.length === 0 ? (
          <ul className="bgSkeletonList" aria-busy="true" aria-label="Loading your records">
            {[0, 1, 2].map((i) => (
              <li key={i} className="bgSkeletonRow" />
            ))}
          </ul>
        ) : records.length === 0 ? (
          <div className="bgEmptyWrap">
            <EmptyState
              title={recordsState.error ? 'Records unavailable' : 'No records yet'}
              subtitle={
                recordsState.error
                  ? 'The records list could not be loaded. Try again shortly.'
                  : 'When you seal a secret, it shows up here with its members and quorum health.'
              }
            />
            <button type="button" className="btn btn-primary" onClick={d.onNewSecret}>
              <Icon icon={Plus} size="sm" /> Seal a secret
            </button>
          </div>
        ) : (
          <RecordsTable records={records} onConvene={(r) => setTarget(r)} />
        )}
      </section>

      {target && !convene.share && (
        <BreakGlassDialog
          target={{
            id: target.id,
            description: target.description,
            thresholdK: target.thresholdK,
            totalN: target.totalN,
          }}
          timeoutSeconds={target.timeoutSeconds}
          busy={convene.busy}
          error={convene.error}
          onConfirm={(reason) =>
            convene.convene(
              {
                id: target.id,
                description: target.description,
                thresholdK: target.thresholdK,
                totalN: target.totalN,
              },
              reason,
            )
          }
          onCancel={() => setTarget(null)}
        />
      )}

      {convene.share && (
        <ShareCeremonyPanel
          share={convene.share}
          onEnter={convene.enter}
          onClose={() => {
            convene.dismissShare();
            setTarget(null);
          }}
        />
      )}

      {joinOpen && <JoinUnsealModal onClose={() => setJoinOpen(false)} />}
    </div>
  );
}

export default Console;
